// -----------------------------------------------------------------------------
// hero.js — Centralized homepage hero-image registry & deterministic selection.
//
// Design goals:
//   • ONE source of truth for every hero variant, its image assets, and its
//     audience mapping. All hero-selection logic lives here.
//   • Deterministic selection — never random. Priority hierarchy documented
//     inline. Fails closed to the protected `texas_flagship`.
//   • Privacy-conscious — no IP, no geolocation, no fingerprinting, no
//     inferred demographics. Only explicit campaign params, session state,
//     same-site referrer, and an approved UTM allowlist.
//   • Performance — the visitor's chosen variant is resolved by an inline
//     blocking <script> in public/index.html BEFORE React hydrates, so only
//     the chosen image is loaded with fetchPriority="high". Prerender output
//     always contains the flagship image (correct fallback for JS-less
//     crawlers).
//   • Kill switch — flipping HERO_KILL_SWITCH.forceFlagship to true forces
//     texas_flagship for every visitor regardless of signal.
//
// STATUS OF THE FOUR NON-FLAGSHIP IMAGES:
//   As of this session, only `texas_flagship` has an approved production
//   image. The other four variants are wired end-to-end but temporarily
//   reference the flagship image with `status: "pending_approval"`. Swapping
//   in an approved image is a one-field change to `image.desktop`/`.mobile`
//   in the variant definition. No component changes required.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// KILL SWITCH — flip forceFlagship to true to serve texas_flagship to every
// visitor, immediately, without deleting any code, config, or asset.
// -----------------------------------------------------------------------------
export const HERO_KILL_SWITCH = Object.freeze({
  // Production posture: forceFlagship = true guarantees every visitor sees
  // the approved texas_flagship image, unconditionally, ahead of any
  // traffic-based signal. This is the required posture until traffic-aware
  // hero switching is explicitly authorized.
  forceFlagship: true,
  // Traffic-aware routing is enabled by default in preview but the effective
  // default in production remains texas_flagship until images are approved.
  // Setting productionActivated to false gates the same behavior at the
  // routing layer — a redundant safety net.
  productionActivated: false,
});

// -----------------------------------------------------------------------------
// HERO REGISTRY — one entry per approved variant. The renderer reads the
// selected variant's `image` block. Additional variants can be added by
// dropping a new object into this array plus an entry in ROUTE_INTENT_MAP.
// -----------------------------------------------------------------------------
export const HERO_VARIANTS = Object.freeze({
  texas_flagship: {
    variant: "texas_flagship",
    role: "texas_flagship",
    status: "protected",
    protected: true,
    audience:
      "Direct visitors · Organic brand traffic · Unknown or missing signals · Any fallback",
    mood: "Texas authority · Heritage · Continuity · Legacy",
    image: {
      // The protected production image — DO NOT rename, replace, or delete.
      // SHA-256: 0032e7b1688d61f8e16471b202ab0ed6e60fdbc38c6c97de8b663d42886c95bf
      desktop: "/hero-lonestar-0.jpg",
      mobile: "/hero-lonestar-0.jpg",
      original: "/hero-lonestar-0.jpg",
      dimensions: { width: 1376, height: 768 },
      format: "jpeg",
      focal: { desktop: "70% 60%", mobile: "70% 50%" },
    },
    alt:
      "Texas hill country at golden hour with the Dallas skyline in the distance and a large bronze Lone Star mounted on a stone wall — Lone Star Counsel",
  },
  transaction: {
    variant: "transaction",
    role: "transaction",
    status: "recommended_awaiting_approval",
    protected: false,
    audience:
      "Asset Purchase Agreement · Buying or selling a Texas business · M&A · LOI · Due diligence · Seller financing",
    mood:
      "Contemporary Texas boardroom. Sophisticated, candid, discreet, commercially serious.",
    image: {
      desktop: "/hero/recommended/transaction.png",
      mobile: "/hero/recommended/transaction.png",
      original: "/hero/recommended/transaction.png",
      dimensions: { width: 1372, height: 764 },
      format: "png",
      focal: { desktop: "70% 60%", mobile: "70% 55%" },
    },
    alt:
      "Texas business principals and professional advisors reviewing transaction documents in a contemporary Texas boardroom — Lone Star Counsel",
    // Content specification for the approved image, retained here so the
    // photo brief and the manifest live in one place.
    imageBrief:
      "Contemporary glass-walled Texas boardroom. Business principals and their advisors reviewing a legal agreement and financial documents. Sophisticated, candid, discreet, commercially serious. Dallas or Texas skyline may be subtly visible. No handshakes · no gavels · no scales of justice · no posed attorneys · no fake signatures.",
  },
  physician_owner: {
    variant: "physician_owner",
    role: "physician_owner",
    status: "recommended_awaiting_approval",
    protected: false,
    audience:
      "Physicians · Medical-practice owners · Healthcare-business clients · Professional-practice succession",
    mood:
      "Physician as sophisticated business owner. Upscale contemporary office or business conference room.",
    image: {
      desktop: "/hero/recommended/physician_owner.png",
      mobile: "/hero/recommended/physician_owner.png",
      original: "/hero/recommended/physician_owner.png",
      dimensions: { width: 1372, height: 764 },
      format: "png",
      focal: { desktop: "72% 55%", mobile: "72% 55%" },
    },
    alt:
      "A Texas physician business owner reviewing practice-transition documents with a professional advisor — Lone Star Counsel",
    imageBrief:
      "A successful physician or medical-practice owner in an upscale contemporary office or business conference room, reviewing business or planning documents with another professional. Physician appears as a sophisticated business owner — not as a patient-care stock-photo subject. No examination rooms · no hospital beds · no lab-coat clichés · no stethoscopes · no medical emergencies.",
  },
  private_wealth: {
    variant: "private_wealth",
    role: "private_wealth",
    status: "recommended_awaiting_approval",
    protected: false,
    audience:
      "High-net-worth families · Estate planning · Trusts · Asset protection · Business succession · Private client",
    mood:
      "Discretion · Trust · Stewardship · Wealth preservation · Succession · Legacy",
    image: {
      desktop: "/hero/recommended/private_wealth.png",
      mobile: "/hero/recommended/private_wealth.png",
      original: "/hero/recommended/private_wealth.png",
      dimensions: { width: 1372, height: 764 },
      format: "png",
      focal: { desktop: "65% 55%", mobile: "65% 55%" },
    },
    alt:
      "A multigenerational Texas family in an elegant modern home reviewing estate and legacy planning materials — Lone Star Counsel",
    imageBrief:
      "A sophisticated couple, family principal, or multigenerational family in an elegant modern Texas home, private office, or refined professional environment. People appear natural and candid — not posed or looking directly into the camera. Mood: discretion, trust, stewardship, wealth preservation, succession, and legacy.",
  },
  south_asian: {
    variant: "south_asian",
    role: "south_asian",
    status: "pending_approval",
    protected: false,
    // Privacy note: this variant may ONLY be selected from an explicit
    // approved campaign parameter, an approved UTM campaign, or a relevant
    // same-site Indian/South-Asian/U.S.-India page. Never inferred.
    audience:
      "Indian and South Asian business owners · Physicians and professionals · Indian-American families · Indian nationals · U.S.–India cross-border planning",
    mood:
      "Success and trusted counsel as the subject. Sophisticated Texas professional environment.",
    image: {
      desktop: "/hero/recommended/south_asian.png",
      mobile: "/hero/recommended/south_asian.png",
      original: "/hero/recommended/south_asian.png",
      dimensions: { width: 1372, height: 764 },
      format: "png",
      focal: { desktop: "68% 55%", mobile: "68% 55%" },
    },
    alt:
      "An Indian-American Texas entrepreneur or professional couple in a sophisticated business environment — Lone Star Counsel",
    imageBrief:
      "A successful Indian-American or South Asian entrepreneur, physician, executive, professional couple, business owner, or family in a sophisticated Texas professional environment. Success and trusted counsel are the subject; ethnicity is not treated as a marketing gimmick. No flags · no Taj Mahal · no traditional-costume stereotypes · no religious imagery · no exaggerated cultural symbolism · no tokenistic diversity imagery.",
  },
});

export const HERO_VARIANT_NAMES = Object.freeze(Object.keys(HERO_VARIANTS));
export const DEFAULT_VARIANT = "texas_flagship";

// -----------------------------------------------------------------------------
// ROUTE-TO-INTENT MAP — same-site referring paths that classify a visitor
// arriving at "/" from another Lone Star Counsel page. Only routes that
// actually exist in this project are listed here.
// -----------------------------------------------------------------------------
export const ROUTE_INTENT_MAP = Object.freeze([
  // -- transaction ------------------------------------------------------------
  { match: /^\/texas-asset-purchase-agreement-attorney\/?$/, intent: "transaction" },
  { match: /^\/business\/asset-purchase-agreement-attorney-texas\/?$/, intent: "transaction" },
  // -- physician_owner --------------------------------------------------------
  { match: /^\/texas-physician-practice-sale-attorney\/?$/, intent: "physician_owner" },
  // -- private_wealth ---------------------------------------------------------
  { match: /^\/texas-business-succession-planning-attorney\/?$/, intent: "private_wealth" },
  { match: /^\/practice\/private-client\/?$/, intent: "private_wealth" },
  // -- south_asian ------------------------------------------------------------
  // No dedicated Indian / South-Asian / U.S.–India pages exist in the current
  // route table. When one is added, list it here to enable same-site mapping.
  // The south_asian variant otherwise requires an explicit lsc_intent or an
  // approved UTM campaign to select.
]);

// -----------------------------------------------------------------------------
// APPROVED UTM ALLOWLIST — campaign / content values that legitimately map to
// a specific hero variant. Every entry is a case-insensitive exact match on
// utm_campaign OR utm_content. Everything not on this list is ignored.
// -----------------------------------------------------------------------------
export const UTM_INTENT_ALLOWLIST = Object.freeze({
  transaction: [
    "apa-2026",
    "texas-business-sale",
    "acquire-texas-business",
    "mna-texas",
  ],
  physician_owner: [
    "physician-sale-2026",
    "medical-practice-sale-texas",
    "mso-texas",
  ],
  private_wealth: [
    "estate-planning-2026",
    "texas-succession",
    "family-office-texas",
  ],
  south_asian: [
    "india-tx-2026",
    "us-india-cross-border",
    "asha-2026",
  ],
  texas_flagship: [
    "brand-2026",
    "lsc-brand",
  ],
});

// -----------------------------------------------------------------------------
// SIGNAL SOURCE CONSTANTS — passed through to analytics as `selection_source`
// so we can see how each variant was chosen.
// -----------------------------------------------------------------------------
export const SELECTION_SOURCES = Object.freeze({
  KILL_SWITCH: "kill_switch",
  EXPLICIT_PARAM: "lsc_intent_param",
  SESSION_CARRY: "session_carry",
  SAME_SITE_REFERRER: "same_site_referrer",
  APPROVED_UTM: "approved_utm",
  DEFAULT_FALLBACK: "default_fallback",
});

// -----------------------------------------------------------------------------
// selectHeroVariant — pure function that resolves the variant + source given
// the raw signals. Never touches DOM or storage. Deterministic and testable.
// -----------------------------------------------------------------------------
export function selectHeroVariant({
  killSwitchForceFlagship = false,
  lscIntentParam = null,
  sessionVariant = null,
  sameSiteReferrerPath = null,
  utmCampaign = null,
  utmContent = null,
} = {}) {
  // 0. Kill switch — highest priority, unconditional.
  if (killSwitchForceFlagship) {
    return { variant: DEFAULT_VARIANT, source: SELECTION_SOURCES.KILL_SWITCH };
  }

  // 1. Explicit campaign parameter (?lsc_intent=…).
  if (lscIntentParam && HERO_VARIANTS[lscIntentParam]) {
    return { variant: lscIntentParam, source: SELECTION_SOURCES.EXPLICIT_PARAM };
  }

  // 2. Session-carried variant — keeps the selected hero stable through
  //    same-session navigation and page refresh.
  if (sessionVariant && HERO_VARIANTS[sessionVariant]) {
    return { variant: sessionVariant, source: SELECTION_SOURCES.SESSION_CARRY };
  }

  // 3. Same-site referrer path — visitor arrived at "/" from another LSC page.
  if (sameSiteReferrerPath) {
    const match = ROUTE_INTENT_MAP.find((r) => r.match.test(sameSiteReferrerPath));
    if (match && HERO_VARIANTS[match.intent]) {
      return { variant: match.intent, source: SELECTION_SOURCES.SAME_SITE_REFERRER };
    }
  }

  // 4. Approved UTM allowlist — utm_campaign OR utm_content match.
  const normalize = (v) => (v || "").toString().trim().toLowerCase();
  for (const [variant, list] of Object.entries(UTM_INTENT_ALLOWLIST)) {
    if (!HERO_VARIANTS[variant]) continue;
    const hitCampaign = list.some((c) => c.toLowerCase() === normalize(utmCampaign));
    const hitContent = list.some((c) => c.toLowerCase() === normalize(utmContent));
    if (hitCampaign || hitContent) {
      return { variant, source: SELECTION_SOURCES.APPROVED_UTM };
    }
  }

  // 5. Fall back to the protected flagship for direct, organic, unknown,
  //    missing, conflicting, or invalid signals.
  return { variant: DEFAULT_VARIANT, source: SELECTION_SOURCES.DEFAULT_FALLBACK };
}
